import { useEffect, useRef, useState, useCallback } from 'react';
import { useApi } from '../../services/api/provider';
import { ValidationJobsService } from '../../services/api/domains/validationJobs/service';
import { subscribeValidationJobsPoll } from './validationJobsPollBus';

/**
 * Poll interval for the nav indicator's active-job count (issue #637,
 * VPE-011 sub-task 11.1) — the first genuine polling infra in the client,
 * deliberately scoped to just this one global widget. 15s balances a
 * reasonably fresh count against not hammering the endpoint; usage is
 * expected to be bursty admin runs, not continuous (Requirement 6's
 * concurrency-design note), so sub-5s polling would be wasted.
 *
 * Shortened under `window.__E2E_TEST__` (same bypass flag as the auth
 * shortcut in client/src/auth/auth.ts) so e2e/validateProducts.spec.ts's
 * nav-indicator assertions don't need to wait out a real 15s cycle against
 * Playwright's 30s default test timeout (Copilot review, PR #683).
 */
const POLL_INTERVAL_MS =
  typeof window !== 'undefined' && window.__E2E_TEST__ === true
    ? 2_000
    : 15_000;

export interface UseValidationJobsSummaryResult {
  activeCount: number;
  isLoading: boolean;
  error: string | null;
}

/**
 * Polls GET /api/validation-jobs/active-summary on an interval. Only polls
 * when `enabled` is true (admin, checked by the caller) — no wasted
 * requests for non-admin users.
 */
export function useValidationJobsSummary(
  enabled: boolean
): UseValidationJobsSummaryResult {
  const api = useApi();
  const service = useRef(new ValidationJobsService(api));
  useEffect(() => {
    service.current = new ValidationJobsService(api);
  }, [api]);

  const [activeCount, setActiveCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards against an in-flight poll's response landing after this effect's
  // own cleanup (disable/unmount) -- clearInterval only stops future ticks,
  // it doesn't cancel a request already in flight. Without this, a late
  // response could set stale state visible on the next re-enable.
  const isCurrentRef = useRef(false);

  const poll = useCallback(async () => {
    if (!isCurrentRef.current) return;
    setIsLoading(true);
    try {
      const response = await service.current.getActiveSummary();
      if (!isCurrentRef.current) return;
      setActiveCount(response.data.active_count);
      setError(null);
    } catch (err) {
      if (!isCurrentRef.current) return;
      setError(
        err instanceof Error ? err.message : 'Failed to load job summary'
      );
    } finally {
      if (isCurrentRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setActiveCount(0);
      setIsLoading(false);
      setError(null);
      return undefined;
    }

    isCurrentRef.current = true;
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    // A job just got triggered (ValidateProductsModal) or a tracked job just
    // left the active list (ValidationJobsPage) -- poll now instead of
    // waiting up to POLL_INTERVAL_MS for the indicator to catch up.
    const unsubscribe = subscribeValidationJobsPoll(poll);
    return () => {
      isCurrentRef.current = false;
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [enabled, poll]);

  return { activeCount, isLoading, error };
}
